import React, { useState } from "react";
import {
  Alert,
  Box,
  Typography,
  useTheme,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import LaunchIcon from "@mui/icons-material/Launch";
import SyncIcon from "@mui/icons-material/Sync";

import Sidebar from "../components/sidebar/Sidebar";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { setToastMessage } from "../store/slices/app/appSlice";
import {
  selectUserDb,
  selectUser,
} from "../store/slices/session/sessionSelector";
import {
  useListBriefingsQuery,
  useDeleteBriefingMutation,
  useSyncCampaignFinancialsMutation,
} from "../store/apis/briefingApi";
import DeleteBriefingButton from "../components/briefings/DeleteBriefingButton";
import { components } from "../types/api";
import BriefingSummaryDrawer from "../components/briefings/BriefingSummaryDrawer";
import CampaignDateRange from "../components/briefings/CampaignDateRange";
import ConfirmationModal from "../components/modals/ConfirmationModal";
import { useContextMenu } from "../hooks/useContextMenu";
import { TableRowContextMenu } from "../components/common/TableRowContextMenu";
import { BriefingStatusChip } from "../components/common/StatusChips";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import AssignedUserFilter from "../components/briefings/AssignedUserFilter";

// Map month number to label
const getMonthOptions = () => [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

type BriefingCategory = components["schemas"]["_36_Enums.BriefingCategory"];

const CATEGORY_OPTIONS: BriefingCategory[] = ["MONTHLY", "BIMONTHLY", "ANNUAL"];

// Local YYYY-MM-DD formatter (avoids UTC shift from toISOString near month edges)
const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const firstDayOfCurrentMonth = () => {
  const now = new Date();
  return toYMD(new Date(now.getFullYear(), now.getMonth(), 1));
};

const lastDayOfCurrentMonth = () => {
  const now = new Date();
  return toYMD(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

const Briefings: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const canCreateBriefings =
    userDb?.role === "ADMIN" || userDb?.role === "ACCOUNT";
  const canViewBriefings =
    canCreateBriefings ||
    userDb?.role === "TECHNICIAN" ||
    userDb?.role === "PRICING";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const defaultFilters = {
    companyName: "",
    search: "",
    month: "",
    year: "",
    minBudget: "",
    maxBudget: "",
    status: [] as string[],
    briefingType: "",
    category: "",
    // Persona asignada a la empresa (partner success o responsable de plataforma)
    assignedUserId: "",
  };

  const getSavedState = () => {
    try {
      const saved = localStorage.getItem("beapolo_briefings_filters");
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error("Failed to parse saved filters", e);
    }
    return null;
  };

  const savedState = getSavedState();

  // Migrate saved filters: status was a string before multi-select, now it must be string[]
  const migratedFilters = React.useMemo(() => {
    if (!savedState?.filters) return defaultFilters;
    const saved = savedState.filters;
    return {
      ...defaultFilters,
      ...saved,
      status: Array.isArray(saved.status)
        ? saved.status
        : saved.status
          ? [saved.status]
          : [],
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [filters, setFilters] =
    useState<typeof defaultFilters>(migratedFilters);
  const [page, setPage] = useState<number>(savedState?.page || 0);
  const [pageSize, setPageSize] = useState<number>(savedState?.pageSize || 25);
  const [sortBy, setSortBy] = useState<
    "createdAt" | "updatedAt" | "name" | "year"
  >(savedState?.sortBy || "updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    savedState?.sortDir || "desc"
  );

  React.useEffect(() => {
    localStorage.setItem(
      "beapolo_briefings_filters",
      JSON.stringify({ filters, page, pageSize, sortBy, sortDir })
    );
  }, [filters, page, pageSize, sortBy, sortDir]);

  const [selectedBriefingId, setSelectedBriefingId] = React.useState<
    string | null
  >(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState<string>(
    firstDayOfCurrentMonth
  );
  const [exportEndDate, setExportEndDate] = useState<string>(
    lastDayOfCurrentMonth
  );
  // "pricing" = the per-briefing pricing sheet; "closing" = the month-end sheet
  // with one row per associated campaign and planned vs real with margin.
  const [exportVariant, setExportVariant] = useState<"pricing" | "closing">(
    "pricing"
  );
  const user = useSelector(selectUser);

  const { contextMenu, handleContextMenu, handleCloseContextMenu } =
    useContextMenu<(typeof briefings)[0]>();
  const [briefingToDelete, setBriefingToDelete] = useState<string | null>(null);
  const [deleteBriefing, { isLoading: isDeleting }] =
    useDeleteBriefingMutation();

  const [syncFinancials, { isLoading: isSyncing }] =
    useSyncCampaignFinancialsMutation();
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  const isAdmin = userDb?.role === "ADMIN";
  const canExportBriefings =
    userDb?.role === "ADMIN" || userDb?.role === "PRICING";

  // Estados visibles por defecto en la lista (todos menos ARCHIVED).
  // Cuando el usuario no selecciona ningún estado, se filtran estos para
  // ocultar los briefings archivados.
  const defaultStatuses = React.useMemo<
    components["schemas"]["BriefingStatus"][]
  >(
    () => [
      "DRAFT",
      "READY_FOR_PRICING",
      "READY_FOR_PRODUCTION",
      "IN_PRODUCTION",
      "FINALIZED",
    ],
    []
  );

  // Opciones del filtro de estado: incluye ARCHIVED para que el usuario
  // pueda mostrar explícitamente los briefings archivados.
  const statusOptions = React.useMemo<
    components["schemas"]["BriefingStatus"][]
  >(() => [...defaultStatuses, "ARCHIVED"], [defaultStatuses]);

  const queryParams = React.useMemo(
    () => ({
      page: page + 1, // API is likely 1-indexed
      pageSize,
      companyName: filters.companyName.trim() || undefined,
      search: filters.search.trim() || undefined,
      // Sin selección explícita ocultamos los archivados enviando solo los
      // estados por defecto; si el usuario filtra (incluyendo ARCHIVED) se
      // respeta su selección.
      status:
        filters.status && filters.status.length > 0
          ? (filters.status as components["schemas"]["BriefingStatus"][])
          : defaultStatuses,
      month: filters.month ? Number(filters.month) : undefined,
      year: filters.year ? Number(filters.year) : undefined,
      minBudget: filters.minBudget ? Number(filters.minBudget) : undefined,
      maxBudget: filters.maxBudget ? Number(filters.maxBudget) : undefined,
      briefingType: filters.briefingType
        ? (filters.briefingType as components["schemas"]["_36_Enums.BriefingType"])
        : undefined,
      category: filters.category
        ? [filters.category as BriefingCategory]
        : undefined,
      assignedUserId: filters.assignedUserId || undefined,
      sortBy,
      sortDir,
    }),
    [filters, page, pageSize, sortBy, sortDir, defaultStatuses]
  );

  const { data, isLoading, isFetching, error, refetch } = useListBriefingsQuery(
    queryParams,
    {
      refetchOnMountOrArgChange: true,
      skip: !canViewBriefings,
    }
  );

  const briefings = data?.data?.items || [];
  const total = data?.data?.total || 0;
  const isEmpty = !isLoading && !isFetching && total === 0;

  const errorMessage =
    error && "data" in error && error.data && typeof error.data === "object"
      ? "message" in error.data
        ? String(error.data.message)
        : t("briefings.errorLoading", "Failed to load briefings")
      : error
        ? t("briefings.errorLoading", "Failed to load briefings")
        : null;

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page on filter change
  };

  const handleSort = (field: "createdAt" | "updatedAt" | "name" | "year") => {
    const isAsc = sortBy === field && sortDir === "asc";
    setSortDir(isAsc ? "desc" : "asc");
    setSortBy(field);
    setPage(0);
  };

  const handleConfirmDelete = async () => {
    if (!briefingToDelete) return;
    try {
      await deleteBriefing({ id: briefingToDelete }).unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefings.deleteSuccess",
            "Briefing deleted successfully"
          ),
          severity: "success",
        })
      );
      if (selectedBriefingId === briefingToDelete) setSelectedBriefingId(null);
      refetch();
    } catch (error) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: t("briefings.deleteError", "Unable to delete briefing"),
          severity: "error",
        })
      );
    } finally {
      setBriefingToDelete(null);
    }
  };

  const handleExportControlPanel = async () => {
    if (!exportStartDate || !exportEndDate) {
      dispatch(
        setToastMessage({
          message: t(
            "briefings.exportDateRangeRequired",
            "Please select a start and end date."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (exportEndDate < exportStartDate) {
      dispatch(
        setToastMessage({
          message: t(
            "briefings.exportEndBeforeStart",
            "End date cannot be before start date."
          ),
          severity: "warning",
        })
      );
      return;
    }
    try {
      setIsExporting(true);
      const url = new URLSearchParams();
      if (queryParams.companyName)
        url.append("companyName", queryParams.companyName);
      if (queryParams.search) url.append("search", queryParams.search);
      if (queryParams.status) {
        queryParams.status.forEach((st) => url.append("status", st));
      }
      if (queryParams.briefingType)
        url.append("briefingType", queryParams.briefingType);
      if (exportStartDate) url.append("startDate", exportStartDate);
      if (exportEndDate) url.append("endDate", exportEndDate);
      if (queryParams.minBudget)
        url.append("minBudget", String(queryParams.minBudget));
      if (queryParams.maxBudget)
        url.append("maxBudget", String(queryParams.maxBudget));
      url.append("variant", exportVariant);

      const API_URL = process.env.REACT_APP_API_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/api/reports/pricing-export/xlsx?${url.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      // The backend now returns a ready-to-use .xlsx file (binary), so we just
      // download the blob directly — no client-side CSV building needed.
      const blob = await response.blob();

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        exportVariant === "closing"
          ? "Cierre_Mes_Export.xlsx"
          : "Pricing_Export.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Export error", err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSyncFinancials = async () => {
    try {
      const res = await syncFinancials().unwrap();
      dispatch(
        setToastMessage({
          message: t(
            "briefings.syncSuccess",
            "Financial sync completed successfully. Processed {{count}} goals.",
            { count: res.processed }
          ),
          severity: "success",
        })
      );
      refetch();
    } catch (error) {
      console.error("Sync error:", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefings.syncFailed",
            "Failed to sync campaign financials"
          ),
          severity: "error",
        })
      );
    } finally {
      setIsSyncModalOpen(false);
    }
  };

  const numberFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  if (!canViewBriefings) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                color: theme.palette.grey[900],
              }}
            >
              {t("sidebar.briefings", "Briefings")}
            </Typography>
            <Typography variant="body2" color={theme.palette.grey[600]}>
              {t("briefings.description", "Manage campaign briefing requests")}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={t("briefings.refreshTooltip", "Refresh list")}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => refetch()}
                  disabled={isFetching || isExporting}
                  sx={{
                    border: `1px solid ${theme.palette.primary.light}`,
                    "&:disabled": {
                      opacity: 0.5,
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            {canExportBriefings && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={
                  isExporting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={() => setIsExportModalOpen(true)}
                disabled={isExporting}
                sx={{
                  borderRadius: "50px",
                  px: 3,
                  fontWeight: 700,
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.05)",
                }}
              >
                {isExporting
                  ? t("briefings.exporting", "Generando CSV...")
                  : t("briefings.exportControlPanel", "Export Pricing CSV")}
              </Button>
            )}
            {canCreateBriefings && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/briefings/new")}
                sx={{
                  borderRadius: "50px",
                  px: 3,
                  fontWeight: 700,
                  boxShadow: "0px 8px 16px rgba(0,0,0,0.08)",
                }}
              >
                {t("briefings.create", "Create briefing")}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outlined"
                color="info"
                startIcon={<SyncIcon />}
                onClick={() => setIsSyncModalOpen(true)}
                disabled={isSyncing}
                sx={{
                  borderRadius: "50px",
                  px: 3,
                  fontWeight: 700,
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.05)",
                }}
              >
                {isSyncing
                  ? t("common.syncing", "Syncing...")
                  : t("briefings.syncFinancials", "Sync Platform Financials")}
              </Button>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontFamily: "Nunito", fontWeight: 700 }}
            >
              {t("sidebar.briefings", "Briefings")}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
            >
              <AssignedUserFilter
                value={filters.assignedUserId}
                onChange={(userId) =>
                  handleFilterChange("assignedUserId", userId)
                }
                currentUserId={userDb?.id}
              />
              <Typography variant="body2" color="text.secondary">
                {t("briefings.totalLabel", "{{count}} total", { count: total })}
              </Typography>
            </Stack>
          </Box>
          <Divider />
          {errorMessage && (
            <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}

          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell
                    sx={{ width: "20%", maxWidth: 150, fontWeight: 700 }}
                  >
                    {t("briefings.columns.client", "Cliente")}
                  </TableCell>
                  <TableCell
                    sx={{ width: "35%", minWidth: 250, fontWeight: 700 }}
                  >
                    {t("briefings.columns.briefingName", "Nombre Briefing")}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 700 }}>
                    {t("briefings.columns.briefingType", "Tipo")}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 700 }}>
                    {t("briefings.columns.category", "Categoría")}
                  </TableCell>
                  <TableCell sx={{ minWidth: 160, fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "year"}
                      direction={sortBy === "year" ? sortDir : "asc"}
                      onClick={() => handleSort("year")}
                    >
                      {t("briefings.columns.date", "Mes/Año")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 700 }}>
                    {t("briefings.columns.budget", "Inversión Plan")}
                  </TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 700 }}>
                    {t("briefings.columns.status", "Estado")}
                  </TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "updatedAt"}
                      direction={sortBy === "updatedAt" ? sortDir : "asc"}
                      onClick={() => handleSort("updatedAt")}
                    >
                      {t("briefings.columns.lastUpdated", "Último cambio")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ minWidth: 100, fontWeight: 700 }}
                    align="right"
                  >
                    {t("briefings.columns.actions", "Acciones")}
                  </TableCell>
                </TableRow>
                {/* Filter Row */}
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell sx={{ py: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      placeholder={t("common.filter", "Filter")}
                      value={filters.companyName}
                      onChange={(e) =>
                        handleFilterChange("companyName", e.target.value)
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      placeholder={t("common.filter", "Filter")}
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        displayEmpty
                        value={filters.briefingType}
                        onChange={(e) =>
                          handleFilterChange(
                            "briefingType",
                            e.target.value as string
                          )
                        }
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        }}
                      >
                        <MenuItem value="">
                          <span style={{ color: theme.palette.text.disabled }}>
                            {t("common.filter", "Filter")}
                          </span>
                        </MenuItem>
                        {["PAID_MEDIA_MANAGED", "BUY_MEDIA_DIRECT_AND_OWN"].map(
                          (type) => (
                            <MenuItem key={type} value={type}>
                              {t(
                                `briefingsCreate.form.briefingTypeOptions.${type}`,
                                type.replace("_", " ")
                              )}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        displayEmpty
                        value={filters.category}
                        onChange={(e) =>
                          handleFilterChange(
                            "category",
                            e.target.value as string
                          )
                        }
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        }}
                      >
                        <MenuItem value="">
                          <span style={{ color: theme.palette.text.disabled }}>
                            {t("common.filter", "Filter")}
                          </span>
                        </MenuItem>
                        {CATEGORY_OPTIONS.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {t(`briefings.category.${cat}`, cat)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <FormControl fullWidth size="small">
                        <Select
                          displayEmpty
                          value={filters.month}
                          onChange={(e) =>
                            handleFilterChange(
                              "month",
                              e.target.value as string
                            )
                          }
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: "20px",
                            "& fieldset": {
                              borderColor: theme.palette.grey[300],
                            },
                          }}
                        >
                          <MenuItem value="">
                            <span
                              style={{ color: theme.palette.text.disabled }}
                            >
                              {t("common.filter", "Filter")}
                            </span>
                          </MenuItem>
                          {getMonthOptions().map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <Select
                          displayEmpty
                          value={filters.year}
                          onChange={(e) =>
                            handleFilterChange("year", e.target.value as string)
                          }
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: "20px",
                            "& fieldset": {
                              borderColor: theme.palette.grey[300],
                            },
                          }}
                        >
                          <MenuItem value="">
                            <span
                              style={{ color: theme.palette.text.disabled }}
                            >
                              {t("common.filter", "Filter")}
                            </span>
                          </MenuItem>
                          {[2023, 2024, 2025, 2026, 2027].map((yr) => (
                            <MenuItem key={yr} value={yr}>
                              {yr}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      type="number"
                      placeholder={t("common.filter", "Filter")}
                      value={filters.minBudget}
                      onChange={(e) =>
                        handleFilterChange("minBudget", e.target.value)
                      }
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        multiple
                        displayEmpty
                        value={filters.status}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleFilterChange(
                            "status",
                            typeof val === "string" ? val.split(",") : val
                          );
                        }}
                        renderValue={(selected) => {
                          if ((selected as string[]).length === 0) {
                            return (
                              <span
                                style={{ color: theme.palette.text.disabled }}
                              >
                                {t("common.filter", "Filter")}
                              </span>
                            );
                          }
                          return (selected as string[])
                            .map((opt) => t(`briefings.status.${opt}`, opt))
                            .join(", ");
                        }}
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: "20px",
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        }}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            <Checkbox
                              checked={filters.status.indexOf(option) > -1}
                            />
                            <ListItemText
                              primary={t(`briefings.status.${option}`, option)}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {/* Empty cell for lastUpdated */}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {/* Empty cell for actions */}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading || isFetching ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : isEmpty ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary" variant="body2">
                        {t("briefings.placeholder", "No briefings yet")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  briefings.map((briefing) => (
                    <TableRow
                      hover
                      key={briefing.id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedBriefingId(briefing.id)}
                      onDoubleClick={() =>
                        navigate(`/briefings/${briefing.id}`)
                      }
                      onContextMenu={(e) => handleContextMenu(e, briefing)}
                    >
                      <TableCell sx={{ maxWidth: 150 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={briefing.companyName || briefing.companyId}
                        >
                          {briefing.companyName || briefing.companyId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-word",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {briefing.name}
                          {(() => {
                            const b = briefing as Record<string, unknown>;
                            if (
                              typeof b.version === "number" &&
                              b.version > 1
                            ) {
                              return (
                                <Chip
                                  label={`v${b.version}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              );
                            }
                            return null;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={briefing.briefingType?.replace("_", " ")}
                          size="small"
                          variant="outlined"
                          sx={{
                            textTransform: "capitalize",
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {briefing.category && (
                          <Chip
                            label={t(
                              `briefings.category.${briefing.category}`,
                              briefing.category
                            )}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.75rem" }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <CampaignDateRange
                          startDate={briefing.campaignsStartDate}
                          endDate={briefing.campaignsEndDate}
                          month={briefing.month}
                          year={briefing.year}
                          variant="body2"
                          layout="horizontal"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {numberFormatter.format(briefing.totalBudget || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <BriefingStatusChip status={briefing.status} />
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={new Date(briefing.updatedAt).toLocaleString()}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {new Date(briefing.updatedAt).toLocaleDateString()}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <Tooltip title={t("common.view", "View")}>
                            <span>
                              <IconButton
                                size="small"
                                color="default"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  window.open(
                                    `/briefings/${briefing.id}`,
                                    "_blank"
                                  );
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canCreateBriefings &&
                            briefing.status !== "FINALIZED" && (
                              <>
                                <Tooltip title={t("common.edit", "Edit")}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="default"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(
                                          `/briefings/${briefing.id}/edit`
                                        );
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={t(
                                    "briefings.delete",
                                    "Delete briefing"
                                  )}
                                >
                                  <span>
                                    <DeleteBriefingButton
                                      briefingId={briefing.id}
                                      onDeleted={() => {
                                        if (
                                          selectedBriefingId === briefing.id
                                        ) {
                                          setSelectedBriefingId(null);
                                        }
                                        refetch();
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </>
                            )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </Box>
      <BriefingSummaryDrawer
        open={!!selectedBriefingId}
        onClose={() => setSelectedBriefingId(null)}
        briefingId={selectedBriefingId}
        canEdit={canCreateBriefings}
      />

      <TableRowContextMenu
        contextMenu={contextMenu}
        onClose={handleCloseContextMenu}
        actions={[
          {
            label: t("common.openInNewTab", "Open in new tab"),
            icon: <LaunchIcon fontSize="small" />,
            onClick: () => {
              if (contextMenu?.data)
                window.open(`/briefings/${contextMenu.data.id}`, "_blank");
            },
          },
          {
            label: t("common.edit", "Edit"),
            icon: <EditIcon fontSize="small" />,
            show:
              canCreateBriefings && contextMenu?.data?.status !== "FINALIZED",
            onClick: () => {
              if (contextMenu?.data)
                navigate(`/briefings/${contextMenu.data.id}/edit`);
            },
          },
          {
            label: t("common.delete", "Delete"),
            icon: <DeleteIcon fontSize="small" />,
            color: "error.main",
            show:
              canCreateBriefings && contextMenu?.data?.status !== "FINALIZED",
            onClick: () => {
              if (contextMenu?.data) setBriefingToDelete(contextMenu.data.id);
            },
          },
        ]}
      />

      <ConfirmationModal
        open={!!briefingToDelete}
        title={t("briefings.deleteConfirmTitle", "Delete briefing")}
        message={t(
          "briefings.deleteConfirmMessage",
          "Are you sure you want to delete this briefing? This action cannot be undone."
        )}
        onConfirm={handleConfirmDelete}
        onCancel={() => setBriefingToDelete(null)}
        isLoading={isDeleting}
      />

      <ConfirmationModal
        open={isSyncModalOpen}
        title={t("briefings.syncModalTitle", "Sync Financial Calculations")}
        message={t(
          "briefings.syncModalContent",
          "This will recalculate expected volume, visibility, CTR and adserver costs for older campaigns based on current platform defaults. Manual margin values are preserved. Proceed?"
        )}
        onConfirm={handleSyncFinancials}
        onCancel={() => setIsSyncModalOpen(false)}
        isLoading={isSyncing}
      />

      <Dialog
        open={isExportModalOpen}
        onClose={() => !isExporting && setIsExportModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          {t("briefings.exportPricing", "Export Pricing CSV")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
        >
          <FormControl fullWidth>
            <InputLabel id="export-variant-label">
              {t("briefings.exportVariant", "Export type")}
            </InputLabel>
            <Select
              labelId="export-variant-label"
              label={t("briefings.exportVariant", "Export type")}
              value={exportVariant}
              onChange={(e) =>
                setExportVariant(e.target.value as "pricing" | "closing")
              }
            >
              <MenuItem value="pricing">
                {t("briefings.exportVariantPricing", "Pricing (per briefing)")}
              </MenuItem>
              <MenuItem value="closing">
                {t(
                  "briefings.exportVariantClosing",
                  "Month-end closing (per associated campaign)"
                )}
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {exportVariant === "closing"
              ? t(
                  "briefings.exportClosingDesc",
                  "One row per associated campaign, with client, campaign, platform, format, funnel stage and dates, plus planned and real investment with margin applied and the variance between them. Amounts are prorated to the chosen range."
                )
              : t(
                  "briefings.exportPricingDesc",
                  "Select the date range to export. Amounts are prorated to the chosen range."
                )}
          </Typography>
          <TextField
            type="date"
            fullWidth
            label={t("common.startDate", "Start date")}
            value={exportStartDate}
            onChange={(e) => setExportStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: exportEndDate || undefined }}
          />
          <TextField
            type="date"
            fullWidth
            label={t("common.endDate", "End date")}
            value={exportEndDate}
            onChange={(e) => setExportEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: exportStartDate || undefined }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsExportModalOpen(false)}
            color="inherit"
            disabled={isExporting}
            sx={{
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "50px",
            }}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => {
              handleExportControlPanel();
              setIsExportModalOpen(false);
            }}
            variant="contained"
            color="primary"
            disabled={
              isExporting ||
              !exportStartDate ||
              !exportEndDate ||
              exportEndDate < exportStartDate
            }
            startIcon={
              isExporting ? <CircularProgress size={20} /> : <DownloadIcon />
            }
            sx={{
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "50px",
            }}
          >
            {isExporting
              ? t("common.exporting", "Exporting...")
              : t("common.download", "Download")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Briefings;
